import {
  Button,
  Divider,
  Icons,
  useDrawer,
  InfoTooltip,
  Stack,
} from '@cast/design-system';

import {
  makeNodeConfigTemplatesLink,
  makeSelectionTemplatesLink,
} from 'features/cluster/autoscaler/utils';
import {
  NodeAttributesDrawer,
  type NodeAttribute,
} from 'features/cluster/nodes/_components/NodeAttributesDrawer';
import { TemplateLinkWithVersion } from 'features/cluster/nodes/_components/TemplateLinkWithVersion';
import {
  getNodeConfigurationName,
  getNodeConfigurationVersion,
  getNodePoolName,
  getNodeClassName,
  getNodeSelectionTemplateName,
  getNodeSelectionTemplateVersion,
} from 'features/cluster/nodes/_utils/common';
import { isNodeUnschedulable } from 'features/cluster/nodes/NodeList/utils';
import { NodeWithDependencies } from 'features/cluster/nodes/types';
import { Cluster } from 'types/cluster';

import { Detail } from './Detail';
import { Section } from './Section';

import { isKentManagedCluster } from '@cast/shared-cluster-context-data-access';

type Props = {
  node: NodeWithDependencies;
  cluster: Cluster;
};

const TITLE = 'Additional information';

const OpenDrawerButton = ({
  onClick,
  testId,
  count,
  disabled,
}: {
  onClick: () => void;
  testId: string;
  count: number;
  disabled: boolean;
}) => {
  return (
    <Button
      variant="text"
      onClick={onClick}
      data-testid={testId}
      disabled={disabled}
      endIcon={<Icons.ArrowRight weight="regular" size={16} />}
    >
      View ({count})
    </Button>
  );
};

export const AdditionalInformationSection = ({ node, cluster }: Props) => {
  const { open } = useDrawer();
  const isKentCluster = isKentManagedCluster(cluster);

  const nodeConfigurationName = getNodeConfigurationName(node);
  const nodeConfigurationVersion = getNodeConfigurationVersion(node);
  const nodeTemplateName = getNodeSelectionTemplateName(node);
  const nodeTemplateVersion = getNodeSelectionTemplateVersion(node);
  const kentNodePoolName = getNodePoolName(node);
  const kentNodeClassName = getNodeClassName(node);

  const handleOpenNodeDataDrawer = (nodeAttribute: NodeAttribute) => {
    open({
      component: NodeAttributesDrawer,
      props: {
        node,
        nodeAttribute,
      },
    });
  };

  const labelsCount = Object.keys(node.labels || {}).length;
  const annotationsCount = Object.keys(node.annotations || {}).length;
  const taintsCount = node.taints?.length || 0;

  const isUnschedulable = isNodeUnschedulable(node);

  return (
    <Section
      title={TITLE}
      contentWrapperProps={{ gap: 0 }}
      testId="node-details-additional-information-section"
    >
      <Stack gap={12} divider={<Divider />}>
        <Stack gap={16}>
          <Detail
            title="Unschedulable:"
            detail={isUnschedulable ? 'Yes' : 'No'}
            testId="node-details-unschedulable"
          />
          <Detail
            title={
              <Stack direction="row" alignItems="center">
                Unremovable
                <InfoTooltip
                  iconSize="14px"
                  title="Node is marked as not to be evicted or deleted."
                  noDefaultIconStyling={false}
                  iconSx={{
                    position: 'relative',
                    top: '-1px',
                  }}
                />
                :
              </Stack>
            }
            detail={node.unremovable ? 'Yes' : 'No'}
            testId="node-details-unremovable"
          />
        </Stack>

        <Stack gap={16}>
          <Detail
            title="Cloud node name:"
            detail={node.instanceName}
            showCopy={true}
            copyValue={node.instanceName || undefined}
            testId="node-details-cloud-node-name"
          />
          <Detail
            title="Cloud node ID:"
            detail={node.instanceId}
            showCopy={true}
            copyValue={node.instanceId || undefined}
            testId="node-details-cloud-node-id"
          />
          <Detail
            title="CAST node ID:"
            detail={node.castNodeId}
            showCopy={true}
            copyValue={node.castNodeId || undefined}
            testId="node-details-cast-node-id"
          />
        </Stack>
        <Stack gap={16}>
          <Detail
            title="Kernel:"
            detail={node.nodeInfo?.kernelVersion}
            testId="node-details-kernel"
          />
          <Detail
            title="Kube proxy:"
            detail={node.nodeInfo?.kubeProxyVersion}
            testId="node-details-kube-proxy"
          />
          <Detail
            title="Kubelet:"
            detail={node.nodeInfo?.kubeletVersion}
            testId="node-details-kubelet"
          />
        </Stack>
        <Stack gap={16}>
          {isKentCluster ? (
            <>
              <Detail
                title="Node pool:"
                detail={kentNodePoolName}
                testId="node-details-node-pool"
              />
              <Detail
                title="Node class:"
                detail={kentNodeClassName}
                testId="node-details-node-class"
              />
            </>
          ) : (
            <>
              <Detail
                title="Node template:"
                detail={
                  nodeTemplateName &&
                  nodeTemplateVersion && (
                    <TemplateLinkWithVersion
                      withVersionPrefix
                      name={nodeTemplateName}
                      version={nodeTemplateVersion}
                      path={makeSelectionTemplatesLink({
                        clusterId: cluster.id,
                        openTemplate: nodeTemplateName,
                      })}
                    />
                  )
                }
                testId="node-details-node-template"
              />
              <Detail
                title="Configuration:"
                detail={
                  nodeConfigurationName &&
                  nodeConfigurationVersion && (
                    <TemplateLinkWithVersion
                      withVersionPrefix
                      name={nodeConfigurationName}
                      version={nodeConfigurationVersion}
                      path={makeNodeConfigTemplatesLink({
                        clusterId: cluster.id,
                        search: {
                          openConfig: nodeConfigurationName,
                        },
                      })}
                    />
                  )
                }
                testId="node-details-configuration"
              />
            </>
          )}
        </Stack>
        <Stack gap={16}>
          <Detail
            title="Labels:"
            detail={
              <OpenDrawerButton
                onClick={() => handleOpenNodeDataDrawer('labels')}
                testId="open-labels-drawer"
                count={labelsCount}
                disabled={!labelsCount}
              />
            }
          />
          <Detail
            title="Annotations:"
            detail={
              <OpenDrawerButton
                onClick={() => handleOpenNodeDataDrawer('annotations')}
                testId="open-annotations-drawer"
                count={annotationsCount}
                disabled={!annotationsCount}
              />
            }
          />
          <Detail
            title="Taints:"
            detail={
              <OpenDrawerButton
                onClick={() => handleOpenNodeDataDrawer('taints')}
                testId="open-taints-drawer"
                count={taintsCount}
                disabled={!taintsCount}
              />
            }
          />
        </Stack>
      </Stack>
    </Section>
  );
};

const AdditionalInformationSectionSkeleton = () => {
  return (
    <Section title={TITLE}>
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
    </Section>
  );
};

AdditionalInformationSection.Skeleton = AdditionalInformationSectionSkeleton;
